// Photo bundle for the site-survey editor: property photos + asset photos
// (same as buildPropertyPhotosBundle) PLUS every PhotosField value and every
// LeafMetadata.evidence URL captured in the survey tree.

import JSZip from "jszip";
import type { BuildContext } from "@/features/exports/services/DownloadManagerContext";
import type { AttributeField } from "@/features/properties/types/propertyAttributes";
import {
  fetchProperty,
  propertyFolderLabel,
  fetchAssetsForProperty,
  fetchAssetPhotos,
  fetchBytes,
  runWithConcurrency,
} from "./shared";
import { sanitize } from "@/features/exports/services/_helpers";

interface Args {
  projectId: string;
  propertyId: string;
  surveyFields: AttributeField[];
}

interface SurveyPhotoRef {
  url: string;
  /** Path segments under the survey/ folder, sanitized. Last segment is base filename without extension. */
  pathSegments: string[];
  /** "photos" leaf value | "evidence" attached to any leaf. */
  kind: "photos" | "evidence";
}

const CONCURRENCY = 6;

/**
 * Walk the survey tree, collecting:
 * - PhotosField values → survey/photos/<question-path>/<n>
 * - Any leaf's `evidence` array → survey/evidence/<question-path>/<n>
 */
function collectSurveyPhotoRefs(fields: AttributeField[]): SurveyPhotoRef[] {
  const out: SurveyPhotoRef[] = [];
  const walk = (nodes: AttributeField[], ancestorTitles: string[]) => {
    for (const node of nodes) {
      const titlePath = [...ancestorTitles, sanitize(node.title || node.id)];
      if (node.type === "node") {
        walk(node.fields, titlePath);
        continue;
      }
      if (node.type === "photos" && Array.isArray(node.value)) {
        node.value.forEach((url, i) => {
          if (typeof url === "string" && url.length > 0) {
            out.push({
              url,
              pathSegments: [...titlePath, String(i + 1).padStart(2, "0")],
              kind: "photos",
            });
          }
        });
      }
      const evidence = (node as { evidence?: unknown }).evidence;
      if (Array.isArray(evidence)) {
        evidence.forEach((url, i) => {
          if (typeof url === "string" && url.length > 0) {
            out.push({
              url,
              pathSegments: [...titlePath, String(i + 1).padStart(2, "0")],
              kind: "evidence",
            });
          }
        });
      }
    }
  };
  walk(fields, []);
  return out;
}

/** Pull a sensible extension from a URL, defaulting to "jpg". */
function extensionFromUrl(url: string): string {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\.([a-zA-Z0-9]{2,5})$/);
    if (m) return m[1].toLowerCase();
  } catch {
    // ignore
  }
  return "jpg";
}

export async function buildSurveyPhotosBundle(
  { propertyId, surveyFields }: Args,
  ctx: BuildContext,
): Promise<Blob> {
  const property = await fetchProperty(propertyId);
  if (!property) throw new Error("Property not found");
  const propertyLabel = propertyFolderLabel(property);

  ctx.setProgress(2, "Loading photo inventory…");

  const assets = await fetchAssetsForProperty(propertyId);
  const assetMap = new Map(assets.map((a) => [a.id, a]));
  const assetPhotos = await fetchAssetPhotos(assets.map((a) => a.id));
  const surveyRefs = collectSurveyPhotoRefs(surveyFields);

  const total = assetPhotos.length + surveyRefs.length;
  if (total === 0) {
    throw new Error("No photos found for this property.");
  }

  const zip = new JSZip();
  let processed = 0;
  const failures: string[] = [];

  const bump = () => {
    processed++;
    const pct = 5 + Math.floor((processed / total) * 90);
    ctx.setProgress(pct, `${processed} / ${total} photos`);
  };

  const tasks: Array<() => Promise<void>> = [];

  for (const photo of assetPhotos) {
    tasks.push(async () => {
      if (ctx.signal.aborted) return;
      if (photo.file_path) {
        const asset = assetMap.get(photo.asset_id);
        const assetLabel = sanitize(asset?.name || photo.asset_id);
        const cat = sanitize(photo.category || "general");
        const buf = await fetchBytes(photo.file_path, {
          bucket: "asset-documents",
          signal: ctx.signal,
        });
        if (buf) {
          const folder = `${propertyLabel}/assets/${assetLabel}/${cat}`;
          const name = sanitize(photo.document_name || photo.id) + ".jpg";
          zip.file(`${folder}/${name}`, buf);
        } else {
          failures.push(`asset photo ${photo.id}`);
        }
      }
      bump();
    });
  }

  for (const ref of surveyRefs) {
    tasks.push(async () => {
      if (ctx.signal.aborted) return;
      const buf = await fetchBytes(ref.url, { signal: ctx.signal });
      if (buf) {
        const ext = extensionFromUrl(ref.url);
        const segments = ref.pathSegments.slice();
        const base = segments.pop()!;
        const folder = [propertyLabel, "survey", ref.kind, ...segments].join("/");
        zip.file(`${folder}/${base}.${ext}`, buf);
      } else {
        failures.push(`survey ${ref.kind} photo ${ref.url}`);
      }
      bump();
    });
  }

  await runWithConcurrency(tasks, CONCURRENCY, ctx.signal);
  if (ctx.signal.aborted) throw new Error("Cancelled");

  if (failures.length > 0) {
    zip.file(
      `${propertyLabel}/MANIFEST.txt`,
      [`Photos that failed to download (${failures.length}):`, ...failures.map((s) => `  ${s}`)].join("\n"),
    );
  }

  ctx.setProgress(96, "Compressing…");
  const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
  ctx.setProgress(100, "Done");
  return blob;
}
